<!-- This component will always take threadDetails as props bc it is never called on its own -->
<!-- All updates will be done through the threads store -->
<script setup>
import stores from "@/stores";
import DepartmentDropdown from "./DepartmentDropdown.vue";
import {
  BIconCircleFill,
  BIconChevronUp,
  BIconChevronDown,
  BIconChatSquareDots,
  BIconSend,
  BIconArrowUp,
  BIconArrowDown,
  BIconArrows,
} from "bootstrap-icons-vue";
import { useToast } from "vue-toastification";
import IconMSTeams from "@/components/icons/IconMSTeams.vue";
import IconChrome from "@/components/icons/IconChrome.vue";
import IconWeb from "@/components/icons/IconWeb.vue";
import IconSlack from "@/components/icons/IconSlack.vue";
import IconWhatsapp from "@/components/icons/IconWhatsapp.vue";
import IconWordpress from "@/components/icons/IconWordpress.vue";
import IconEmail from "@/components/icons/IconEmail.vue";
import IconSMS from "@/components/icons/IconSMS.vue";
import IconMeta from "@/components/icons/IconMeta.vue";
import IconFeedback from "@/components/ui-kit/base/IconFeedback.vue";
import IconPriority from "@/components/icons/IconPriority.vue";
import { TAB_VALUE } from "@/constants/interaction";
import { useThreadsContextStore } from "@/stores/threads";
import ListLoader from "./ui-kit/base/ListLoader.vue";
import { useNavigation } from "@/composables/useNavigation";
import { useThreadDisplay } from "@/composables/useThreadDisplay";
import ThreadStatusDropdown2 from "./ThreadStatusDropdown2.vue";
import CloseTicketModal from "@/components/ui-kit/modals/CloseTicketModal.vue";
import { formatInteraction } from "@/api/interactions.js";
import { ThreadStatusEnum, TicketCategoryStatusEnum } from "@/utils/constants";
import { useTicketResponses } from "@/composables/useTicketResponses";

const props = defineProps({
  threadDetails: {
    type: Object,
    required: true,
  },
  selectedThreadIds: {
    type: Array,
    default: [],
  },
});

const toast = useToast()
const emit = defineEmits(["toggle-checked"]);

const ticketInteraction = computed(() => {
  const { ticket_interaction_id: ticketInteractionId, interactions } =
    props.threadDetails;

  if (!ticketInteractionId) return null;
  return interactions.find((i) => i.id === ticketInteractionId);
});

const lastInteraction = computed(() => props.threadDetails.interactions[0]);

const formattedInteractions = computed(() =>
(props.threadDetails.interactions ?? []).map((i) =>
formatInteraction({ ...i, isReadOnly: true })
)
);

const userStore = stores.useUserStore();
const teamStore = stores.useTeamStore();
const threadContextStore = useThreadsContextStore();
const { queryParams, navigateTo } = useNavigation();
const { lastTicketResponse, count } = useTicketResponses(props.threadDetails)

const thread = computed(() => props.threadDetails);
const { nameWithLocationDisplay, timeDisplay } = useThreadDisplay(thread);

// STATES
// ************************************************
const isAssignedToMe = computed(() => {
  const { assignees, teams } = props.threadDetails
  const userId = userStore.authUserId

  // can be part of a team assigned to the thread or directly an assignee
  const threadAssignees = []

  if(assignees.length) {
    threadAssignees.push(...assignees)
  }

  if(teams?.length) {
    const teamAssigneeIds = teams.flatMap(teamId => teamStore.userIds(teamId))
    threadAssignees.push(...teamAssigneeIds)
  }

  return threadAssignees.includes(userId)
})

const threadDetailsTooltipText = computed(() =>
  !isThreadDetailsOpen.value ? "Open details" : "Close details"
);

// TODO: refactor based on excel sheet
// const isWaitingAgentResponse2 = computed(() => {
//   const {  _id , is_form_response, user_id } = lastTicketResponse.value
//   const { assignees, teams } = props.threadDetails

//   if (!_id) return true
//   if(is_form_response) return true
//   if(user_id === userStore.authUserId) return false

//   // check assignees and teams
//   if(assignees.length && !assignees.includes(user_id)) return true
//   if(teams && !teamStore.userIds(teams[0]).includes(user_id)) return true

//   return false;
// });

const isWaitingAgentResponse = computed(() => {
  const {_id, is_form_response, to_user_ids} = lastTicketResponse.value
  const { user_id: threadUserId } = props.threadDetails
  // if its a webform then make true
  // if the lastTicketResponse is going to the ticket creator, make it false
  if(!_id) return true
  if(is_form_response) return true
  if(to_user_ids.includes(threadUserId)) return false

  return true
})

const isWaitingAgentResponseTooltip = computed(() => {
  if(!ticketInteraction.value) return "Not a Ticket"
  return isWaitingAgentResponse.value ? "Response Needed" : "Response Sent"
});

const threadCardActions = computed(() => {
  const userId = userStore.authUserId;
  const seenBy = props.threadDetails.seen_by ?? [];

  const isSeen = seenBy.includes(userId)

  const toggleSeenBy = async (isSeen) => {
    await threadContextStore.patchSeenBy(
      props.threadDetails._id,
      !isSeen
    );
  };

  return [
    {
      label: isSeen ? "Mark as unread" : "Mark as read",
      onClick: () => toggleSeenBy(isSeen),
    },
  ];
});

const markedAsRead = computed(() => {

  const userId = userStore.authUserId;
  const includedStatuses = [
    ThreadStatusEnum.NEW,
    ThreadStatusEnum.REOPENED,
    ThreadStatusEnum.ESCALATED,
    ThreadStatusEnum.OPEN,
    ThreadStatusEnum.PENDING_USER_REPLY,
    ThreadStatusEnum.SOFT_ALERT,
  ]
  const seenBy = props.threadDetails.seen_by || [];

  if(!includedStatuses.includes(props.threadDetails.status)) return true

  if(seenBy.includes(userId) || !isAssignedToMe.value) return true

  return false

});

const isThreadChecked = computed({
  get: () => props.selectedThreadIds.includes(props.threadDetails._id),
  set: () => toggleThreadChecked(),
});
const isThreadHovered = ref(false);
const isThreadDetailsOpen = ref(false);

// METHODS
// ************************************************
// pass in an obj of properties you want to change on the thread level. Only updates those properties keep the rest same
const patchThread = async (payload = {}) => {
  console.log(
    `patching thread. id: ${props.threadDetails._id}, payload: ${JSON.stringify(payload, null, 2)}`
  );
  await threadContextStore.patchThread(props.threadDetails._id, payload);
};

const toggleIsThreadDetailsOpen = () => {
  isThreadDetailsOpen.value = !isThreadDetailsOpen.value;
};

const convertToTicket = async (newStatus) => {
  const threadId = props.threadDetails._id
  try {
    await threadContextStore.patchToTicket(threadId)

    if (
      Array.isArray(queryParams.value.status) &&
      !queryParams.value.status.includes(newStatus)
    ) {
      threadContextStore.removeThreadFromSortedArray(props.threadDetails._id);
    }
  } catch(err) {
    toast.error("Could not convert to ticket")
  }

}

const navigateToThreadDetails = () => {
  const threadId = props.threadDetails._id
  const isTicket = Object.values(TicketCategoryStatusEnum).includes(props.threadDetails.status)

  if(isTicket) threadContextStore.patchSeenBy(threadId, true)
  navigateTo(`/inbox/${threadId}`);
};

const onThreadHover = (state) => {
  isThreadHovered.value = state;
};

const toggleThreadChecked = () => {
  emit("toggle-checked", props.threadDetails._id);
};

// MOVE THIS INTO ITS OWN COMPONENT
const currentIcon = computed(() => {
  const list = props.threadDetails.interactions;
  if (!list?.length) return null;

  const channel = list.at(-1)?.channel?.toLowerCase();

  switch (channel) {
    case "email":
      return IconEmail;
    case "sms":
      return IconSMS;
    case "whatsapp":
      return IconWhatsapp;
    case "slack":
      return IconSlack;
    case "meta":
      return IconMeta;
    case "msteams":
      return IconMSTeams;
    case "wordpress":
      return IconWordpress;
    case "chrome-extension":
      return IconChrome;
    case "web":
      return IconWeb;
    default:
      return IconWeb;
  }
});

const handleStatusUpdate = async (newStatus) => {
  await patchThread({ status: newStatus });
  if (
    Array.isArray(queryParams.value.status) &&
    !queryParams.value.status.includes(newStatus)
  ) {
    threadContextStore.removeThreadFromSortedArray(props.threadDetails._id);
  }
};

const isCloseTicketModalOpen = ref(false);

const handleTicketClose = async (sendNotification = false) => {
  const payload = { status: ThreadStatusEnum.SUPPORT_CLOSED };
  if (sendNotification) {
    payload.send_email = true;
  }
  try {
    await patchThread(payload);
    isCloseTicketModalOpen.value = false;
    if (
      Array.isArray(queryParams.value.status) &&
      !queryParams.value.status.includes(ThreadStatusEnum.SUPPORT_CLOSED)
    ) {
      threadContextStore.removeThreadFromSortedArray(props.threadDetails._id);
    }
  } catch (err) {
    toast.error("Unable to close ticket. Please try again.");
  }
};
</script>

<template>
  <div
    class="interaction-card"
    :class="{
      'has-alert-marker': !markedAsRead,
      'allow-hover': isThreadHovered,
      selected: isThreadChecked,
    }"
    @click="navigateToThreadDetails"
    @mouseenter="onThreadHover(true)"
    @mouseleave="onThreadHover(false)"
  >
    <ListLoader :show="threadContextStore.isLoadingById[threadDetails._id] && !isCloseTicketModalOpen" />
    <div
      class="card-header d-flex align-items-center"
      :class="{
        'allow-sticky': isThreadDetailsOpen,
      }"
    >
      <div
        class="me-2 py-2 px-3"
        v-b-tooltip.hover="threadDetailsTooltipText"
        @click.stop="toggleIsThreadDetailsOpen"
      >
        <BIconChevronDown
          v-if="isThreadDetailsOpen"
          width="14"
          height="14"
          style="font-weight: bold"
        />
        <BIconChevronUp v-else width="14" height="14" />
      </div>
      <IconPriority
        :thread-id="threadDetails._id"
        :ticket-priority-id="threadDetails.ticket_priority_id"
      />

      <div class="position-relative mx-3">
        <icon-marker class="position-relative" size="big">
          <custom-checkbox
            v-if="isThreadHovered || isThreadChecked"
            @click.stop="toggleThreadChecked(!isThreadChecked)"
            :model-value="isThreadChecked"
            :value="true"
            :multiple="false"
          />
          <component v-else :is="currentIcon" :width="18" :height="18" />
        </icon-marker>

        <div v-if="currentIcon">
          <div v-if="lastInteraction.is_user_prompt" class="interaction-arrow">
            <IconIncomingArrow />
          </div>
          <div v-else class="interaction-arrow">
            <IconOutComingArrow />
          </div>
        </div>
        <BIconCircleFill class="circle-fill" v-if="!markedAsRead" />
      </div>

      <div
        :class="{
          'ms-4': shouldDisplayChannelIcon,
        }"
        class="d-flex justify-content-between align-items-center flex-grow-1"
      >
        <div class="interaction-content">
          <div
            class="interaction-title text-neutral-450"
            :class="{ 'bold-text': !markedAsRead }"
          >
            <span>{{
              threadDetails.user_input_summary?.title ||
              threadDetails.thread_summary?.title ||
              "No Title"
            }}</span>
            <IconFeedback
              class="feedback-icon tooltip-semibold"
              v-if="
                ticketInteraction &&
                (ticketInteraction.has_feedback ||
                  ticketInteraction.has_thumbs_up ||
                  ticketInteraction.has_thumbs_down)
              "
              :width="16"
              :height="16"
            />
          </div>

          <div
            class="interaction-subtitle text-neutral-300 d-flex align-items-center column-gap-1"
          >
            <span
              class="text-decoration-underline"
              v-b-tooltip.hover.top="threadDetails.short_id || 'short id'"
            >
              #{{ threadDetails.short_id }}
            </span>
            <IconFrame />
            <span v-b-tooltip.hover.top="nameWithLocationDisplay.tooltip">
              {{ nameWithLocationDisplay.title }}
            </span>
            <IconFrame />
            <span v-b-tooltip.hover.top="timeDisplay.tooltip">
              {{ timeDisplay.title }}
            </span>
          </div>
        </div>

        <div
          class="d-flex align-items-center right-side-card"
          style="min-width: 550px"
          :class="{ 'my-column-gap-4': !showCountersAndTrainingState }"
        >
          <div class="d-flex">
            <ThreadStatusDropdown2
              class="status-dropdown"
              :status="props.threadDetails.status"
              use-close-ticket-modal
              @update-status="handleStatusUpdate"
              @request-close-ticket="isCloseTicketModalOpen = true"
              @convert-to-ticket="convertToTicket"
            />
          </div>
          <div :class="{ 'flex-item': showCountersAndTrainingState }">
            <!-- pass a function for the updated event bc departmentDropdown might be used in other components before refactor -->
            <DepartmentDropdown
              class="department-dropdown"
              v-b-tooltip.hover.top="'Assigned Department'"
              @updated="
                (departmentId) => patchThread({ department: departmentId })
              "
              :dptId="props.threadDetails.department"
              :organizationId="props.threadDetails.organization_id"
            />
          </div>
          <div>
            <div class="ticket-interaction-icons">
              <!-- add waiting for ticket received or ticket sent -->
              <div v-b-tooltip.hover="isWaitingAgentResponseTooltip">
                <BIconArrows
                  v-if="!ticketInteraction"
                  height="15"
                  width="15"
                />
                <BIconArrowDown
                  v-else-if="isWaitingAgentResponse"
                  height="15"
                  width="15"
                  style="color: lightcoral"
                />
                <BIconArrowUp
                  v-else
                  height="15"
                  width="15"
                  style="color: lightseagreen"
                />
              </div>
              <div
                class="d-flex align-items-center text-neutral-300"
                @click.stop="navigateToThreadDetails"
              >
                <BIconChatSquareDots
                  v-b-tooltip.hover.top="
                    `${threadDetails.interactions.length} interactions`
                  "
                />
                <span class="count ms-1 font-size-xs">
                  {{ threadDetails.interactions.length }}
                </span>
              </div>
              <div
                class="messages-counts d-flex align-items-center text-neutral-300"
              >
                <IconMessages
                  v-b-tooltip.hover.top="
                    `${count} emails`
                  "
                />
                <span class="count ms-1 font-size-xs">
                  {{ count }}
                </span>
              </div>
              <div
                class="comments-counts d-flex align-items-center text-neutral-300"
                @click.stop="navigateToThreadDetails"
              >
                <IconComment
                  v-b-tooltip.hover.top="
                    `${ticketInteraction?.nb_comments || 0} comments`
                  "
                />
                <span class="count ms-1 font-size-xs">
                  {{ ticketInteraction?.nb_comments || 0 }}
                </span>
              </div>
            </div>
          </div>
          <div>
            <div class="training-assignee">
              <!-- THIS BECOMES THREAD LEVEL CONSTRUCT -->
              <assignee-dropdown
                show-teams-tab
                :agent-list="userStore.activeHqUsers"
                :team-list="teamStore.validList"
                :preselected-agents="threadDetails.assignees || []"
                :preselected-teams="threadDetails.teams || []"
                @update-assignees="(obj) => patchThread(obj)"
                @update-teams="(obj) => patchThread(obj)"
              />
            </div>
          </div>

          <three-dots-dropdown
            :actions="threadCardActions"
            style="margin-left: 16px"
            :class="{ 'is-disabled': !isAssignedToMe }"
          >
            <template #MarkAsUnread-icon>
              <IconRead />
            </template>
          </three-dots-dropdown>
        </div>
      </div>
    </div>
    <!-- TAGS WILL BE A THREAD LEVEL CONSTRUCT TOO -->
    <!-- <div v-if="isTicket" class="mt-3 d-flex align-items-center">
      <div v-if="true" class="tags d-flex column-gap-2 me-2">
        <tag
          v-for="(tag, idx) in ticketInteraction.user_tags"
          :key="idx"
          :tag="tag"
        />
      </div>
      <AddTagsDropdown :tags="tagsOptions" />
    </div> -->
    <div v-if="isThreadDetailsOpen" class="mt-4 thread-details">
      <div class="d-flex justify-content-between w-100">
        <section class="section summary-section">
          <div
            v-if="
              threadDetails.user_input_summary?.summary ||
              threadDetails.thread_summary?.summary
            "
          >
            <span class="summary-title mb-2">Summary</span>
            <span>
              {{
                threadDetails.user_input_summary?.summary ||
                threadDetails.thread_summary?.summary
              }}
            </span>
          </div>

          <div v-else class="no-summary">No ticket summary</div>
        </section>
        <section class="section interactions-section">
          <div
            v-for="interaction in formattedInteractions"
            :key="interaction.id || interaction._id"
          >
            <InteractionCommunicationCard
              :interaction="interaction"
              :showTrainingButton="false"
              size="small"
            >
              <template #top-right-corner>
                <span></span>
              </template>
            </InteractionCommunicationCard>
          </div>
        </section>
      </div>
    </div>

    <close-ticket-modal
      v-model="isCloseTicketModalOpen"
      @close-only="handleTicketClose()"
      @close-and-notify="handleTicketClose(true)"
      :is-empty-ticket="true"
    />
  </div>
</template>

<style scoped lang="scss">
$--min-width: 950px;

.flip-x-y {
  transform: scale(-1, -1);
  // transform: scaleX(-1);
}

.interaction-arrow {
  border-radius: 100px;
  border: 2px solid $neutral-0;
  background: $neutral-150;
  display: flex;
  padding: var(--padding-xs, 4px);
  align-items: center;
  gap: 10px;
  width: fit-content;
  position: absolute;
  left: -5px;
  bottom: -5px;
}

.interaction-card {
  position: relative;
  min-width: $--min-width;
  padding: 0 1rem 0.5rem 1.5rem;
  background-color: white;
  cursor: pointer;
  border-bottom: 1px solid $neutral-150;

  &.allow-hover {
    background: $neutral-50;
  }

  &.selected {
    background: var(--primary-0) !important;
  }

  :deep(.icon-marker) {
    background: $neutral-0;
  }

  &.has-alert-marker {
    background: $neutral-50;

    :deep(.department-dropdown) {
      .btn {
        background: $neutral-50;
      }
    }

    .three-dots-dropdown {
      :deep(.dropdown) {
        .three-dots-vertical-icon {
          path {
            fill: $neutral-400;
          }
        }
      }
    }
  }

  .transit-icon {
    position: absolute;
    right: 22px;
    top: 20px;
    border: 2px solid $bluish-gray-100;
    background: $bluish-gray-300;
    color: $bluish-gray-900;
    border-radius: 50%;
  }

  .circle-fill {
    position: absolute;
    width: 12px;
    height: 12px;
    left: 34px;
    bottom: 34px;
    color: var(--primary-250);
  }

  .interaction-title {
    font-size: $font-size-md;
    color: $neutral-450;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow-y: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 1;
    // border: 1px solid black;
    max-width: 400px;

    // @media (min-width: 1600px) {
    //   max-width: 400px;
    // }

    @media (min-width: 1800px) {
      max-width: 500px;
    }

    @media(min-width: 2000px) {
      max-width: 700px;
    }

    &.bold-text {
      font-weight: bold;
    }

    .feedback-icon {
      margin-left: 0.75rem;

      :deep(path) {
        fill: $neutral-300;
      }
    }
  }

  .interaction-content {
    min-width: 425px;
    margin-right: 10px;
    // border: 1px solid black;
    max-width: 50%;
  }

  .interaction-subtitle {
    font-size: 14px;
    //> span:nth-child(1n) {
    //  white-space: nowrap;
    //  overflow: hidden;
    //  text-overflow: ellipsis;
    //  text-align: left;
    //}
  }

  .flex-item {
    min-width: 150px;
    // flex-grow: 1;
  }

  .training-assignee {
    // border: 1px black solid;
    width: 75px;
    flex-grow: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .ticket-interaction-icons {
    // border: 1px black solid;
    width: 175px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-left: 2rem;
    margin-right: 2rem;
    gap: 1.5rem;

    svg {
      display: block;
      width: 18px;
      min-width: 18px;
    }


    // .ticket-interaction-icons .bi-send-arrow-down,
    // .ticket-interaction-icons .bi-send-check {
    //   position: relative;
    //   top: -1px;
    // }

    .message-delivered,
    .messages-counts,
    .comments-counts,
    .training-state {
      :deep(svg) {
        path {
          fill: $neutral-300;
        }
      }

      .count {
        color: $neutral-300;
      }
      &:hover {
        :deep(svg) {
          path {
            fill: $neutral-400;
          }
        }

        .count {
          color: $neutral-400;
        }
      }
    }
  }
}

.thread-details {
  background-color: $neutral-50;
  font-size: 14px;
  // color: $neutral-600;
  color: $neutral-350;
}

.section {
  padding: 1em;
  max-height: 600px;
  overflow-y: auto;
  background-color: white;
  border-radius: 8px;
  border: 1px solid $neutral-150;

  &.summary-section {
    flex-grow: 1;
    max-width: 30%;

    .no-summary {
      text-align: center;
      padding: 1em;
      font-style: italic;
    }
  }

  &.interactions-section {
    flex-grow: 1;
    max-width: 68%;
  }
}

.card-header {
  padding-top: 2rem;
  padding-bottom: 2rem;

  &.allow-sticky {
    position: sticky;
    top: 0;
    border-top: 1px solid $neutral-150;
    z-index: 10;
    background: white;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.status-dropdown {
  // width: 150px;
  width: 125px;
  text-align: right;
  :deep(.btn) {
    width: 100%;
  }

  :deep(.dropdown-toggle > span) {
    display: flex !important;
    justify-content: flex-start !important;
  }

  :deep(.dropdown-toggle > span > svg:last-child) {
    margin-left: auto !important;
  }

  :deep(.dropdown-toggle > span > span) {
    min-width: 0;
    max-width: 60%;
    display: block;
    // border: 1px solid black;
    overflow: hidden;
    text-overflow: ellipsis !important;
    white-space: nowrap;
  }
}

.department-dropdown {
  width: 100px;
  // border: 1px solid black;
}

.summary-title {
  display: block;
  text-decoration: underline;
}

.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}
</style>
